import React, { useEffect, useRef, useState } from "react";

const siteKey =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6LdN7W4tAAAAAO53JiHnC2MkfNDDLS6C-3C3W841";

let recaptchaLoader;

function loadRecaptcha() {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (recaptchaLoader) return recaptchaLoader;

  recaptchaLoader = new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let timer;

    const waitUntilReady = () => {
      if (window.grecaptcha?.render) {
        window.clearTimeout(timer);
        resolve(window.grecaptcha);
        return;
      }

      if (Date.now() - startedAt > 10000) {
        reject(new Error("Google reCAPTCHA did not initialise."));
        return;
      }

      timer = window.setTimeout(waitUntilReady, 50);
    };

    window.fhbPaymentRecaptchaReady = waitUntilReady;

    const existingScript = document.getElementById("google-recaptcha-script");
    if (existingScript) {
      waitUntilReady();
      return;
    }

    const script = document.createElement("script");
    script.id = "google-recaptcha-script";
    script.src =
      "https://www.google.com/recaptcha/api.js?onload=fhbPaymentRecaptchaReady&render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener(
      "error",
      () => {
        window.clearTimeout(timer);
        reject(new Error("Google reCAPTCHA could not be loaded."));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return recaptchaLoader;
}

export default function RecaptchaCheckbox({ onChange, resetSignal }) {
  const lightHostRef = useRef(null);
  const darkHostRef = useRef(null);
  const widgetIdsRef = useRef({ light: null, dark: null });
  const [theme, setTheme] = useState(() =>
    document.documentElement.dataset.theme === "dark" ? "dark" : "light"
  );
  const [error, setError] = useState(
    siteKey ? "" : "CAPTCHA is not configured."
  );
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const root = document.documentElement;
    const updateTheme = () => {
      setTheme(root.dataset.theme === "dark" ? "dark" : "light");
    };
    const observer = new MutationObserver(updateTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    updateTheme();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!siteKey) return undefined;
    let active = true;
    const host = theme === "dark" ? darkHostRef.current : lightHostRef.current;

    loadRecaptcha()
      .then((grecaptcha) => {
        if (
          !active ||
          !host?.isConnected ||
          widgetIdsRef.current[theme] !== null
        ) {
          return;
        }

        widgetIdsRef.current[theme] = grecaptcha.render(host, {
          sitekey: siteKey,
          theme,
          size: window.matchMedia("(max-width: 380px)").matches
            ? "compact"
            : "normal",
          callback: (token) => {
            if (themeRef.current !== theme) return;
            setError("");
            onChange(token);
          },
          "expired-callback": () => {
            if (themeRef.current !== theme) return;
            onChange("");
            setError("The CAPTCHA expired. Please complete it again.");
          },
          "error-callback": () => {
            if (themeRef.current !== theme) return;
            onChange("");
            setError("The CAPTCHA could not be loaded. Please try again.");
          },
        });
      })
      .catch((loadError) => {
        if (active) setError(loadError.message);
      });

    return () => {
      active = false;
    };
  }, [onChange, theme]);

  useEffect(() => {
    Object.values(widgetIdsRef.current).forEach((widgetId) => {
      if (widgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(widgetId);
      }
    });
    onChange("");
  }, [onChange, theme]);

  useEffect(() => {
    if (resetSignal > 0 && window.grecaptcha) {
      Object.values(widgetIdsRef.current).forEach((widgetId) => {
        if (widgetId !== null) window.grecaptcha.reset(widgetId);
      });
      onChange("");
    }
  }, [onChange, resetSignal]);

  return (
    <div className="recaptcha-field">
      <div ref={lightHostRef} hidden={theme !== "light"} />
      <div ref={darkHostRef} hidden={theme !== "dark"} />
      {error && <small role="alert">{error}</small>}
    </div>
  );
}
